import { datadogRum } from "@datadog/browser-rum";

const SSO_IMPLEMENTATION_KEY = "sso-implementation";
export type SSOImplementation = "spaSSO" | "externalSSO";
const SSO_IMPLEMENTATIONS: ReadonlySet<string> = new Set<SSOImplementation>([
  "spaSSO",
  "externalSSO",
]);
const DEFAULT_SSO_IMPLEMENTATION: SSOImplementation = "spaSSO";
let cachedSSOImplementation: SSOImplementation | undefined;

/**
 * Query params that opted into the retired `newSSO` and `cognitoSSO` implementations. Old
 * bookmarks and the deprecated SSO page still send people here with them, so we strip them.
 */
const RETIRED_SSO_QUERY_PARAMS = ["newSSO", "cognitoSSO"];

/**
 * The one-time `code` the retired `cognitoSSO` flow got back on the query string. `spaSSO`'s MSAL
 * redirect comes back in the hash, not the query, so outside `externalSSO` this is just a stale
 * credential sitting in the address bar (and in the post-login resume path, see
 * `new-msal/redirect.ts`).
 *
 * Cognito sent `code` alone. An OAuth2 authorization response pairs it with `state` (RFC 6749
 * §4.1.2) — that's how the integrations OEM wizards (`useCompleteConnection`) get theirs back — so
 * a `code` that arrives with `state` is someone else's callback and is left alone.
 *
 * `accessToken`/`access_token` are deliberately left alone: they're how the mobile app hands
 * `externalSSO` its starting token, and that contract is out of scope here.
 */
const RETIRED_SSO_CALLBACK_PARAMS = ["code"];

/** localStorage keys left behind by the retired `newSSO` and `cognitoSSO` implementations. */
const RETIRED_SSO_STORAGE_KEYS = [
  "corteva-access-token",
  "corteva-identity-token",
  "corteva-refresh-token",
  "corteva-auth-mutex",
];

const isSSOImplementation = (
  value: string | null,
): value is SSOImplementation =>
  value !== null && SSO_IMPLEMENTATIONS.has(value);

/**
 * Track which auth provider is being used in Datadog RUM. Only tracks once per page load.
 *
 * This is useful for tracking which auth provider is being used, and for debugging issues. SSO
 * page is unauthenticated, so we can't use the user ID to track it, but we do have the
 * localStorage key.
 */
let authProviderTracked = false;
const trackAuthProvider = (implementation: SSOImplementation) => {
  if (authProviderTracked) return;
  authProviderTracked = true;
  datadogRum.setGlobalContextProperty("sso_provider", implementation);
  datadogRum.addAction("auth_provider_selected", { provider: implementation });
};

/**
 * Clean up after the retired `newSSO` and `cognitoSSO` implementations: drop their stored tokens,
 * and strip their query params from the URL without adding a history entry.
 */
const cleanUpRetiredSSO = (implementation: SSOImplementation) => {
  for (const key of RETIRED_SSO_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }

  const url = new URL(globalThis.location.href);
  const isOAuthCallback = url.searchParams.has("state");
  const paramsToStrip =
    implementation === "externalSSO" || isOAuthCallback
      ? RETIRED_SSO_QUERY_PARAMS
      : [...RETIRED_SSO_QUERY_PARAMS, ...RETIRED_SSO_CALLBACK_PARAMS];
  const strippedParams = paramsToStrip.filter((param) =>
    url.searchParams.has(param),
  );
  if (strippedParams.length === 0) return;

  for (const param of strippedParams) {
    url.searchParams.delete(param);
  }
  globalThis.history.replaceState(globalThis.history.state, "", url);
  datadogRum.addAction("auth_retired_sso_params_stripped", {
    params: strippedParams,
  });
};

/**
 * Resolve the SSO implementation once per page load. Query params win over localStorage, which
 * wins over the default.
 *
 * Anything stored that isn't a current implementation (in practice, `newSSO` or `cognitoSSO` from
 * before they were retired) is dropped in favor of the default, so those users land on spaSSO
 * instead of an "Unsupported SSO implementation" error.
 */
export const getSSOImplementation = (): SSOImplementation => {
  if (cachedSSOImplementation) return cachedSSOImplementation;

  const searchParams = new URLSearchParams(globalThis.location.search);
  const storedValue = localStorage.getItem(SSO_IMPLEMENTATION_KEY);
  const isStaleStoredValue =
    storedValue !== null && !isSSOImplementation(storedValue);
  if (isStaleStoredValue) {
    localStorage.removeItem(SSO_IMPLEMENTATION_KEY);
  }

  let implementation: SSOImplementation;
  if (searchParams.get("spaSSO") === "true") {
    implementation = "spaSSO";
    localStorage.setItem(SSO_IMPLEMENTATION_KEY, implementation);
  } else if (searchParams.get("externalSSO") === "true") {
    implementation = "externalSSO";
    localStorage.setItem(SSO_IMPLEMENTATION_KEY, implementation);
  } else if (isSSOImplementation(storedValue)) {
    implementation = storedValue;
  } else {
    implementation = DEFAULT_SSO_IMPLEMENTATION;
  }

  if (isStaleStoredValue) {
    datadogRum.addAction("auth_provider_migrated", {
      from: storedValue,
      to: implementation,
    });
  }

  cleanUpRetiredSSO(implementation);

  cachedSSOImplementation = implementation;
  trackAuthProvider(implementation);
  return implementation;
};

export const setSSOImplementation = (implementation: SSOImplementation) => {
  localStorage.setItem(SSO_IMPLEMENTATION_KEY, implementation);
  cachedSSOImplementation = implementation;
};
